import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthConfig, OAuthService, TokenResponse } from 'angular-oauth2-oidc';
import { environment } from 'env/config';
import {
  BehaviorSubject,
  combineLatest,
  from,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { catchError, filter, map, shareReplay, tap } from 'rxjs/operators';
import { commonConstants } from '../constants/common.constants';
import { AuthPrivilege, PageAction } from '../models';
import { AuthorizationService } from '../services';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class PrimaveraAuthService {
  public privileges: AuthPrivilege[] = [];
  public isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  public isRefreshTokenInProgress = false;
  public tokenRefreshed = new Subject();

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or error, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values) => values.every((b) => b)));

  private readonly rootPath = '/app/';

  constructor(
    public oauthService: OAuthService,
    private router: Router,
    private authorizationService: AuthorizationService
  ) {
    const authConfig = this.getAuthConfig();
    if (authConfig) {
      this.configure(authConfig);
    }

    // This is tricky, as it might cause race conditions (where access_token is set in another
    // Tab before everything is said and done there.
    window.addEventListener('storage', (event) => {
      if (
        (event.key === 'auth_data_updated' && event.newValue !== null) ||
        event.key === null
      ) {
        this.isAuthenticatedSubject$.next(
          this.oauthService.hasValidAccessToken()
        );

        if (!this.oauthService.hasValidAccessToken()) {
          this.navigateToLoginPage();
        }
      }
    });

    this.oauthService.events.subscribe((_) => {
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );
    });

    this.oauthService.events
      .pipe(
        filter((e) => ['session_terminated', 'session_error'].includes(e.type))
      )
      .subscribe(() => this.navigateToLoginPage());
  }

  // for debugging it makes sense.
  public get accessToken() {
    return this.oauthService.getAccessToken();
  }

  public get identityClaims() {
    return environment.production ? '' : this.oauthService.getIdentityClaims();
  }

  public get idToken() {
    return environment.production ? '' : this.oauthService.getIdToken();
  }

  public get logoutUrl() {
    return environment.production ? '' : this.oauthService.logoutUrl;
  }

  public refreshToken(): Observable<TokenResponse> {
    return from(this.oauthService.refreshToken());
  }

  public refreshTokenObservable(): Observable<any> {
    // check if the token call is in progress, if yes then return empty observable
    if (this.isRefreshTokenInProgress) {
      return of();
    }

    // return refresh token call observable
    this.isRefreshTokenInProgress = true;
    return from(this.oauthService.refreshToken());
  }

  public navigateToLoginPage(isBeingLoggedOut = false, data = '') {
    const self = this;
    self.logout();

    if (isBeingLoggedOut && data) {
      sessionStorage.setItem('isBeingLoggedOut', JSON.stringify(data));
    }

    return this.router.navigateByUrl('/login');
  }

  public configure(authConfig: AuthConfig) {
    this.oauthService.configure(authConfig);
  }

  /**
   * Get Privileges by page
   *
   * @param pageName Page of the path you want to access (route name)
   */
  public getPrivilegesByPage(pageName: string): AuthPrivilege[] {
    return this.privileges.filter(
      (privilege) => privilege.function.toLowerCase() === pageName.toLowerCase()
    );
  }

  /**
   * Try to find specific privilege for role
   *
   * @param pageName Page of the path you want to access (route name)
   * @param action PageAction you want to trigger over that route
   */
  public getPrivilege(pageName: string, action: PageAction) {
    const filteredArray = this.getPrivilegesByPage(pageName);
    return filteredArray.find((privilege) => privilege.action === action);
  }

  /**
   * Check if action exists
   *
   * @param pageName Name of page
   * @param action PageAction to trigger
   */
  public doesActionExist(pageName: string, action: PageAction) {
    return this.getPrivilege(pageName, action) !== undefined;
  }

  public getActionsByPage(path) {
    return this.getPrivilegesByPage(path);
  }

  public isAllowedToAction(path, action: PageAction) {
    let pageName;
    try {
      pageName = path.replace(this.rootPath, '');
    } catch (e) {
      pageName = path;
    }
    return this.doesActionExist(pageName, action);
  }

  public isAllowedToEnterPage(path) {
    return this.isAllowedToAction(path, PageAction.allow);
  }

  canDoAction(page, action: PageAction) {
    if (!page) {
      return false;
    }

    // This is an specific case, it should be manage by BE, BUT, even tho, is safe
    // to assume this table is ok because is for the review of addresses in
    // walklist creation
    if (page === 'd2Cgeoserviceaddressreviewtable') {
      return true;
    }

    const possiblePage = page
      .toLowerCase()
      .replace('page', '')
      .replace('-details', '')
      .replace('detail', '')
      .replace('-view', '')
      .replace('new', '')
      .replace('columns', '')
      .replace('-history', '360')
      .replace('drop-walklist', 'walklist')
      .replace('sales-walklist', 'walklist')
      .replace('d2cgeoserviceaddress', 'walklist')
      .replace('.', '');
    const authorizeActions = this.getActionsByPage(possiblePage).filter(
      (privilege) => privilege.action === action
    );
    return authorizeActions.length !== 0;
  }

  canCreate(page) {
    return this.canDoAction(page, PageAction.post);
  }

  canDelete(page) {
    return this.canDoAction(page, PageAction.delete);
  }

  canUpdate(page) {
    return this.canDoAction(page, PageAction.put);
  }

  canFetch(page) {
    return this.canDoAction(page, PageAction.get);
  }

  getFetchAction(page): AuthPrivilege {
    if (!page) {
      return null;
    }

    const possiblePage = page.toLowerCase().replace('page', '');
    const actions = this.getActionsByPage(possiblePage).filter(
      (privilege) => privilege.action === PageAction.get
    );

    return actions.length !== 0 ? actions[0] : null;
  }

  public arePrivilegesLoaded() {
    return of(true);
  }

  /**
   * Load privileges for Role
   */
  public configurePrivileges(): Observable<boolean> {
    if (this.privileges.length !== 0) {
      return of(true);
    } else {
      return this.fetchPrivileges().pipe(
        shareReplay(1),
        tap((result) => {
          const role = result.items[0];
          const entitlements = role.entitlement as AuthPrivilege[];
          // This is on purpose, because we can't set it on creation, but we don't want to modify this later
          // @ts-ignore
          this.privileges = Object.freeze(
            entitlements.map((entitlement) =>
              Object.freeze({
                action: entitlement.action,
                function: entitlement.function,
                service: entitlement.service,
                condition: entitlement.condition,
                fields: entitlement.fields,
                fieldsCondition: entitlement.fieldsCondition,
                id: entitlement.id,
              })
            )
          );
        }),
        map(() => true),
        catchError(async (error) => {
          console.error(
            'PrimaveraAuthService: configurePrivileges error:',
            error
          );
          return false;
        })
      );
    }
  }

  // ------------------------------------------------
  /**
   * This is the logic for authentication
   */
  /*
  public runInitialLoginSequence(): Promise<boolean> {
    //Load configure again

    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    const authConfig = this.getAuthConfig();
    if (authConfig) {
      this.configure(authConfig);
    } else {
      return new Promise((resolve) => {
        this.isDoneLoadingSubject$.next(true);
        resolve(true);
      });
    }

    //TODO:  JKWS should be fix for production
    //this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    return new Promise((resolve) => {
      this.oauthService
        .tryLogin()
        .then(() => {
          this.isDoneLoadingSubject$.next(true);
          resolve(true);
        })
        .catch((err) => {
          this.isDoneLoadingSubject$.next(true);
          this.navigateToLoginPage(true, err);
          resolve(true);
        });
    });
  }
  */

  // Primavera Auth's runInitialLoginSequence
  public runInitialLoginSequence(): Promise<boolean> {
    return new Promise((resolve) => {
      const hash = window.location.hash;
      const idToken = hash.replace('#id_token=', '');

      if (idToken) {
        sessionStorage.setItem('id_token', idToken);
        sessionStorage.setItem('access_token', idToken);
        sessionStorage.setItem(
          'id_token_stored_at',
          moment().valueOf().toString()
        );
        sessionStorage.setItem(
          'expires_at',
          moment().add(8, 'hours').valueOf().toString()
        );

        // set sessionStorage for SSO type depends on url contains team or partner
        if (window.location.href.includes('team')) {
          sessionStorage.setItem('sso', 'team');
        } else if (window.location.href.includes('partner')) {
          sessionStorage.setItem('sso', 'vendor');
        } else {
          sessionStorage.setItem('sso', ''); // Default to team if not specified
        }

        sessionStorage.setItem('session_state', 'undefined');

        // get user info from id_token
        const idTokenClaims = this.decodeJwtWithoutVerification(idToken);

        sessionStorage.setItem(
          'id_token_claims_obj',
          JSON.stringify(idTokenClaims)
        );

        // Clear the hash to prevent issues with routing
        window.location.hash = '';
      }

      // Always signal that initial login sequence is complete
      this.isDoneLoadingSubject$.next(true);

      resolve(true);
    });
  }

  public decodeJwtWithoutVerification(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const decodedPayload = atob(base64);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  public login(targetUrl?: string) {
    // if logged in, redirect to targetUrl
    if (this.hasValidToken()) {
      if (targetUrl) {
        this.router.navigateByUrl(targetUrl);
        return;
      } else {
        this.router.navigateByUrl('/login');
        return;
      }
    } else {
      window.location.reload();
      //window.location.href = environment.baseUrl + '/login';
      //this.router.navigateByUrl('/login');
    }

    /*
    const authConfig = this.getAuthConfig();
    if (authConfig) {
      console.log('PrimaveraAuthService.login() - configuring auth');
      this.configure(authConfig);
    } else {
      console.log('PrimaveraAuthService.login() - no authConfig found, redirecting to login');
      return this.router.parseUrl('/login');
    }

    const typeSSO = sessionStorage.getItem('sso');
    const header = typeSSO === 'vendor' ? environment.oauth.headers.vendor : {};

    this.oauthService.initCodeFlow(targetUrl || this.router.url, header);
    return false;
    */
  }

  public logout(goToLogin: boolean = true): void {
    try {
      // before clear session storage, need to get sso type
      const ssoType = sessionStorage.getItem('sso');

      localStorage.clear();
      sessionStorage.clear();

      this.clearCookie(ssoType);

      if (goToLogin) {
        if (ssoType === 'team') {
          window.location.href = `${environment.kongLogoutTeamURL}?TargetResource=${environment.redirectUri}`;
        } else {
          window.location.href = `${environment.kongLogoutURL}&logoutUrl=${environment.redirectUri}`;
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  private clearCookie(ssoType: string | null) {
    if (ssoType === 'team') {
      document.cookie = `${environment.appName}-session=;expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${environment.appTeamDomain}; path=/;`;
    } else {
      document.cookie = `${environment.appName}-session=;expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${environment.appDomain}; path=/;`;
    }

    document.cookie = `pf_ptnr_id=;expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${environment.ptnrDomain}; path=/;`;

    // Clear all remaining cookies using native JavaScript
    this.deleteAllCookies();
  }

  /**
   * Delete all cookies using native JavaScript
   */
  private deleteAllCookies(): void {
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      if (name) {
        // Clear cookie for current domain and path
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;

        // Clear cookie for current domain and root path
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname};`;

        // Clear cookie for parent domain (if subdomain)
        const domainParts = window.location.hostname.split('.');
        if (domainParts.length > 2) {
          const parentDomain = '.' + domainParts.slice(-2).join('.');
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${parentDomain};`;
        }

        // Clear cookie for environment baseUrl domain if different
        if (
          environment.baseUrl &&
          environment.baseUrl !== window.location.hostname
        ) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${environment.baseUrl};`;
        }
      }
    }
  }

  public refresh() {
    this.oauthService.silentRefresh();
  }

  public hasValidToken() {
    //return this.hasValidAccessToken() && this.hasValidIdToken();
    return this.hasValidAccessToken();
  }

  // primavera access token expires_at is already set to 8 hours
  public hasValidAccessToken(): boolean {
    const token = sessionStorage.getItem('access_token');

    if (token) {
      const expiresAt = sessionStorage.getItem('expires_at') ?? '0';
      const now = new Date();
      const tokenTTL = new Date(+expiresAt);

      const isValid =
        tokenTTL.getTime() - now.getTime() <= 0
          ? false
          : !(expiresAt && parseInt(expiresAt + 1000, 10) < now.getTime());

      return isValid;
    } else {
      return false;
    }
  }

  /**
   * Checks whether there is a valid id_token.
   */
  public hasValidIdToken(): boolean {
    if (this.oauthService.getIdToken()) {
      const expiresAt = sessionStorage.getItem('id_token_expires_at') ?? '0';
      const now = new Date();
      const tokenTTL = new Date(+expiresAt);
      tokenTTL.setTime(tokenTTL.getTime() + 8 * 60 * 60 * 1000);

      return tokenTTL.getTime() - now.getTime() <= 0
        ? false
        : !(expiresAt && parseInt(expiresAt + 1000, 10) < now.getTime());
    }

    return false;
  }

  getPageActions(page: string, possibleActions: string[]): string[] {
    const authorizeActions = this.getActionsByPage(page);
    const pageActions = authorizeActions
      .filter((privilege) => possibleActions.includes(privilege.action))
      .map((privilege) => privilege.action);

    return pageActions;
  }

  getAccessToken(): Observable<any> {
    const accessToken = sessionStorage.getItem('access_token');

    return new Observable((observer: any) => {
      observer.next(accessToken || '');
    });
  }

  private getAuthConfig(): any {
    const typeSSO = sessionStorage.getItem('sso');
    if (!typeSSO) {
      return '';
    }
    return typeSSO === 'vendor'
      ? environment.oauth.vendor
      : environment.oauth.team;
  }

  private fetchPrivileges() {
    return this.authorizationService
      .getAll({
        service: 'd2c-central',
      })
      .pipe(shareReplay(1));
  }
}
